import { CampaignModel, CampaignStatus } from '@/ui'
import ctl from '@netlify/classnames-template-literals'
import { format, isValid } from 'date-fns'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EditScheduleSheet } from './edit-schedule-sheet'

export type TimelinePanelProps = {
	campaign: CampaignModel
	isLoading: boolean
}

type TimelineStageStatus = {
	status: CampaignStatus
	label: string
	description: string
	submittedBy?: string | null
	icon: ReactNode
	date: string | null
	active: boolean
	isNext: boolean
	isCurrent: boolean
	isFirst: boolean
	isLast: boolean
}

export function TimelinePanel({ campaign, isLoading }: TimelinePanelProps) {
	const { t } = useTranslation('common')
	const stages = useEvaluateTimelineStages(campaign)
	const [isEditingSchedule, setIsEditingSchedule] = useState(false)

	const canEditSchedule = !isLoading && (campaign.status === 'review' || campaign.status === 'active')

	return (
		<div className="space-y-4 mt-2">
			{/* Header */}
			<div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold text-grey-900">{t('overview.timeline.title')}</h2>
				{canEditSchedule && (
					<button
						type="button"
						onClick={() => setIsEditingSchedule(true)}
						className="text-sm font-medium text-brand-500 hover:text-brand-600"
					>
						{t('overview.schedule.edit')}
					</button>
				)}
			</div>

			{canEditSchedule && isEditingSchedule && campaign.startedAt && campaign.endsAt && (
				<EditScheduleSheet
					onClose={() => setIsEditingSchedule(false)}
					campaignId={campaign.id}
					startDate={campaign.startedAt}
					endDate={campaign.endsAt}
				/>
			)}

			{/* Timeline */}
			<div className="mt-8">
				{!isLoading && (
					<div className="space-y-0 flex flex-col">
						{stages.map((stage, index) => (
							<TimelineStage
								key={index}
								stage={stage}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	)
}

function TimelineStage({ stage }: {
	stage: TimelineStageStatus
}) {
	const { t } = useTranslation('common')
	const lineColor = stage.active ? 'bg-purple-500' : 'bg-purple-200'
	const iconBgColor = stage.active ? 'bg-[#511FEC1A]' : 'bg-[#0000001A]'
	const iconTextColor = stage.active ? 'text-[#511FEC]' : 'text-black/50'
	const textColor = stage.active ? 'text-grey-900' : 'text-grey-500'
	const descriptionColor = stage.active ? 'text-grey-600' : 'text-grey-400'

	const dotClasses = stage.active
		? 'bg-purple-500 border-2 border-purple-500'
		: 'bg-white border-2 border-brand-800/30'

	const date = useMemo(() => {
		return (stage.active && stage.date) ? stage.date : ''
	}, [stage.active, stage.date])

	return (
		<div className="relative grid grid-cols-[14px_64px_1fr] gap-4 items-center">
			{/* Progress Bar Column */}
			<div className="relative h-full">
			</div>

			{/* Icon */}
			<div
				className={ctl(`
					${iconBgColor} ${iconTextColor} 
					p-3
					rounded-lg 
					flex items-center justify-center
					flex-shrink-0
					size-16
					mt-2 mb-2 
					overflow-hidden
				`)}
			>
				{stage.icon}

				<div className="absolute inset-y-0 -translate-x-[50px] flex items-center justify-center">
					{/* Solid purple line, only if active or is next */}
					{(stage.active || stage.isNext) && (
						<div
							className={ctl(`
								${
								stage.isFirst
									? 'bg-gradient-to-b from-brand-500/0 via-brand-500 to-brand-500 rounded-t-full'
									: 'bg-brand-500'
							}
								w-[2px] h-[calc(50%-12px)] rounded-b-full
								absolute inset-y-0 left-1/2 -translate-x-1/2
							`)}
						/>
					)}

					{/* Grey line, if not active and is not next */}
					{(!stage.active && !stage.isNext) && (
						<div
							className={ctl(`
								bg-brand-800/20
								w-[2px] h-[calc(50%-12px)] rounded-b-full
								absolute inset-y-0 left-1/2 -translate-x-1/2
							`)}
						/>
					)}

					{/* Arrowhead, if is next */}
					{stage.isNext && (
						<>
							<div
								className={ctl(`
								bg-brand-500
								w-[2px] h-[10px]
								rotate-50
								z-50
								rounded-full
								translate-x-[4px] translate-y-[-15px]
							`)}
							/>

							<div
								className={ctl(`
								bg-brand-500
								w-[2px] h-[10px]
								-rotate-50
								z-50
								rounded-full
								translate-x-[-4px] translate-y-[-15px]
							`)}
							/>
						</>
					)}

					{/* Solid purple line, only if is active */}
					{stage.active && (
						<div
							className={ctl(`
							bg-brand-500
							w-[2px] h-[calc(50%-12px)]
							rounded-t-full
							absolute inset-y-0 left-1/2 -translate-x-1/2
								top-[calc(50%+12px)]
							`)}
						/>
					)}

					{/* Grey line, if not active and not last */}
					{(!stage.active && !stage.isLast) && (
						<div
							className={ctl(`
								bg-brand-800/20
								w-[2px] h-[calc(50%-12px)]
								rounded-t-full
								top-[calc(50%+12px)]
								absolute inset-y-0 left-1/2 -translate-x-1/2
							`)}
						/>
					)}

					{/* Circular Progress Dot */}
					<div
						className={ctl(`
						${dotClasses}
						w-2.5 h-2.5 
						rounded-full
						absolute z-10 -translate-x-1/2 left-1/2
					
					`)}
					/>
				</div>
			</div>

			{/* Content */}
			<div className="my-2">
				{/* Status label with date */}
				<div className="flex items-baseline gap-2">
					<span className={ctl(`text-xs font-medium uppercase tracking-wide ${textColor}`)}>
						{date}
					</span>
				</div>
				{/* Title */}
				<div className={ctl(`font-semibold ${textColor}`)}>
					{stage.label}
				</div>
				{/* Description */}
				<div className={ctl(`text-sm ${descriptionColor}`)}>
					{stage.description}
				</div>
				{/* Submitted by */}
				{stage.submittedBy && (
					<div className={ctl(`text-xs mt-1 ${descriptionColor}`)}>
						{t('overview.timeline.submittedBy', { name: stage.submittedBy })}
					</div>
				)}
			</div>
		</div>
	)
}

function useEvaluateTimelineStages(campaign: CampaignModel): TimelineStageStatus[] {
	const { t } = useTranslation('common')
	const formatDate = useCallback((date?: Date | null) =>
		date && isValid(date)
			? format(date, 'dd MMM yyyy')
			: null, [])

	const status = campaign.status

	const isDraft = status === 'draft'
	const isReview = status === 'review'
	const isPublishing = status === 'publishing'
	const isActive = status === 'active'
	const isCompleted = status === 'completed'

	const stages: TimelineStageStatus[] = [
		{
			status: 'review',
			label: t('overview.timeline.sentToReview'),
			description: t('overview.timeline.sentToReviewDesc'),
			submittedBy: campaign?.submittedForReviewBy,
			icon: <SentToReviewIcon />,
			date: formatDate(campaign?.submittedForReviewAt ?? undefined) ?? formatDate(campaign?.createdAt),
			active: !isDraft,
			isCurrent: isReview,
			isNext: false, // never next, since it's first
			isFirst: true,
			isLast: false,
		},
		{
			status: 'publishing',
			label: t('overview.timeline.reviewFinished'),
			description: t('overview.timeline.reviewFinishedDesc'),
			submittedBy: campaign?.reviewedBy,
			icon: <ReviewFinishedIcon />,
			date: campaign?.reviewedAt
				? formatDate(campaign.reviewedAt)
				: (isActive || isCompleted ? t('overview.timeline.done') : t('overview.timeline.inDays')),
			active: isPublishing || isActive || isCompleted,
			isCurrent: isPublishing,
			isNext: isReview,
			isFirst: false,
			isLast: false,
		},
		{
			status: 'active',
			label: t('overview.timeline.campaignStart'),
			description: t('overview.timeline.campaignStartDesc'),
			icon: <CampaignLaunchIcon />,
			date: formatDate(campaign?.metaAdCampaign?.startDate ?? campaign?.startedAt),
			active: isActive || isCompleted,
			isNext: isPublishing,
			isCurrent: isActive,
			isFirst: false,
			isLast: false,
		},
		{
			status: 'completed',
			label: t('overview.timeline.campaignEnd'),
			description: t('overview.timeline.campaignEndDesc'),
			icon: <CampaignEndIcon />,
			date: formatDate(campaign?.metaAdCampaign?.endDate ?? campaign?.endsAt),
			active: isCompleted,
			isNext: isActive,
			isCurrent: isCompleted,
			isFirst: false,
			isLast: true,
		},
	]

	// Evergreen campaigns publish to the career site with no Meta launch or end date,
	// so the last two Meta stages collapse into a single ongoing career-site stage.
	if (campaign.isEvergreen) {
		return [
			stages[0],
			stages[1],
			{
				status: 'active',
				label: t('overview.timeline.publishedToCareerSite'),
				description: t('overview.timeline.careerSiteOngoing'),
				icon: <CampaignLaunchIcon />,
				date: null,
				active: isActive || isCompleted,
				isNext: isPublishing,
				isCurrent: isActive,
				isFirst: false,
				isLast: true,
			},
		]
	}

	return stages
}

function SentToReviewIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
			<path
				d="M1.545504 0.034032C1.337376 0.099192 1.15968 0.26748 1.069104 0.485232C1.023888 0.593976 1.021536 0.888792 1.064904 1.0101600000000002C1.081848 1.05756 1.12728 1.13772 1.165848 1.188336C1.2044400000000002 1.238928 3.425688 3.6890400000000003 6.102 6.633C8.778288 9.576960000000001 10.968 11.992104 10.968 11.999976C10.968 12.007848000000001 8.76396 14.438376000000002 6.070104 17.401152C3.376272 20.363927999999998 1.143912 22.835856 1.1093039999999998 22.89432C1.057704 22.981488000000002 1.044576 23.038368000000002 1.0363680000000002 23.209992C1.027512 23.395176 1.03332 23.434584 1.0867200000000001 23.551224C1.1604 23.712144 1.296288 23.852304 1.453848 23.929848L1.572 23.988 11.9688 23.99412C22.33608 24.000192000000002 22.365984 24.000072000000003 22.49472 23.951904C22.657248000000003 23.891112 22.832664 23.72568 22.909416 23.560824C22.997304 23.372040000000002 22.992384 23.120448 22.897104000000002 22.92984C22.840656 22.816920000000003 21.827592 21.689544 17.929104000000002 17.401152C15.235680000000002 14.438376000000002 13.032 12.007871999999999 13.032 12C13.032 11.992128000000001 15.235680000000002 9.561624 17.929104000000002 6.598848C21.827592 2.3104560000000003 22.840656 1.1830800000000001 22.897104000000002 1.07016C22.992384 0.8795520000000001 22.997304 0.62796 22.909416 0.439176C22.832736 0.274464 22.657224000000003 0.108888 22.495008000000002 0.048192C22.366728000000002 0.000192 22.323287999999998 0 12.005088 0.001608C3.638856 0.002904 1.6250399999999998 0.009144000000000001 1.545504 0.034032M3.504 1.522872C3.504 1.5424560000000003 11.982168 10.86 12 10.86C12.017832 10.86 20.496 1.5424560000000003 20.496 1.522872C20.496 1.516896 16.672800000000002 1.512 12 1.512C7.3272 1.512 3.504 1.516896 3.504 1.522872M7.741608 17.803008000000002L3.4951920000000003 22.476 7.7476080000000005 22.482072000000002C10.086408 22.485408000000003 13.913592 22.485408000000003 16.252416 22.482072000000002L20.504856 22.476 16.261632 17.808C13.927848000000001 15.2406 12.011568 13.137744 12.003192 13.135008000000001C11.994840000000002 13.132248 10.07712 15.232848 7.741608 17.803008000000002"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.024}
			/>
		</svg>
	)
}

function ReviewFinishedIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
			<path
				d="M3.528 0.015576000000000001C2.3030880000000002 0.097008 1.241784 0.725472 0.571488 1.766352C0.37968 2.064216 0.183432 2.5343999999999998 0.1002 2.895528C-0.003384 3.344904 -0.0026880000000000003 3.295008 0.004824 9.768L0.012 15.972 0.075936 16.092C0.152664 16.236024 0.285096 16.366656 0.432 16.443168C0.5816640000000001 16.52112 0.8956080000000001 16.52364 1.059576 16.448184C1.209072 16.3794 1.3589760000000002 16.224816 1.436448 16.059552L1.5 15.924 1.5126240000000002 9.636000000000001C1.524432 3.7572479999999997 1.52796 3.339144 1.5669119999999999 3.2117999999999998C1.67856 2.846712 1.796352 2.618328 2.014848 2.3433840000000004C2.310816 1.970928 2.714352 1.7161440000000001 3.225288 1.5791279999999999C3.4867440000000003 1.5090000000000001 3.958584 1.503984 4.233384 1.5683999999999998C4.884048 1.720896 5.422368 2.132688 5.733912 2.716224C6.2020800000000005 3.593088 6.02184 4.68576 5.293848 5.383872C4.9047600000000005 5.757000000000001 4.42872 5.959632 3.8514720000000002 5.99784C3.522984 6.01956 3.4014720000000005 6.06096 3.2412000000000005 6.205776C3.185688 6.255912 3.111408 6.351648 3.076128 6.418488000000001L3.012 6.54 3.0053520000000002 14.592C3.000552 20.426856 3.006072 22.688496 3.0254640000000004 22.805616C3.110904 23.321928 3.5791920000000004 23.824632 4.10508 23.9646C4.302408 24.017112 16.697592 24.017112 16.894920000000003 23.9646C17.287704 23.860056 17.639927999999998 23.569176 17.844024 23.180808C18.011256000000003 22.86252 17.999112 23.522688 17.999736000000002 14.724L18.000336 6.756 18.079776 6.701904C18.12348 6.672168 18.254472 6.553368000000001 18.370872 6.437904C18.977352 5.836368 19.340232 5.121984 19.464984 4.284C19.503864 4.022784000000001 19.498944 3.4433759999999998 19.4556 3.179232C19.383432000000003 2.7395280000000004 19.285872 2.446224 19.075752 2.03724C18.631728 1.173048 17.857584000000003 0.510336 16.933775999999998 0.203544C16.622424 0.100152 16.387248 0.053064 16.03764 0.024096C15.788328 0.0034319999999999997 3.8330400000000004 -0.004704 3.528 0.015576000000000001M6.768 1.5204C6.768 1.525488 6.817848 1.6039679999999998 6.878784 1.694832C7.088256 2.007168 7.287072 2.449344 7.380984 2.81184C7.4898240000000005 3.2318640000000003 7.5263279999999995 3.8718 7.464984 4.284C7.383504 4.831367999999999 7.178952 5.3754480000000004 6.892055999999999 5.808L6.77268 5.988 11.372328 5.994504C16.428552 6.001656 16.118232 6.009888 16.53612 5.857368C17.78988 5.399808 18.363528000000002 3.895464 17.733912 2.716224C17.481 2.242512 17.043120000000002 1.8516000000000001 16.56048 1.6686720000000002C16.151856000000002 1.513776 16.56732 1.525968 11.43 1.518216C8.865888 1.514352 6.768 1.515336 6.768 1.5204M16.212 7.477583999999999C16.041552 7.501848 14.784984000000001 7.508712 10.254 7.510152000000001L4.5120000000000005 7.5120000000000005 4.5120000000000005 15.012L4.5120000000000005 22.512 10.5 22.512L16.488 22.512 16.488 14.976C16.488 8.961384 16.481952 7.440696 16.458000000000002 7.443432C16.441512 7.445304 16.3308 7.460688000000001 16.212 7.477583999999999M21.305664 8.30208C20.557704 8.465568000000001 19.96596 8.953416 19.673112 9.648C19.486536 10.09056 19.5 9.64668 19.5 15.353472L19.5 20.446944000000002 20.309376 22.065936C20.754552 22.956384 21.152112 23.722920000000002 21.192888 23.769336C21.23364 23.815776 21.332856 23.886648 21.413328 23.926872C21.539376 23.989872 21.58596 24 21.749328 24C21.902231999999998 24 21.963768 23.988264 22.066920000000003 23.939472C22.308432 23.825208 22.328808 23.791272 23.189832000000003 22.067832000000003L23.988 20.470248 23.988 15.293111999999999L23.988 10.116 23.92188 9.905232C23.672591999999998 9.110568 23.075688 8.526648 22.308 8.326416C22.026287999999997 8.252928 21.58008 8.242104 21.305664 8.30208M12.432 9.82836C12.32388 9.879744 12.137136 10.053456 11.4 10.788336000000001L10.5 11.685576000000001 9.96 11.150736C9.476496 10.671816 9.405479999999999 10.610448 9.281088 10.563936C9.000216 10.458912 8.695751999999999 10.5168 8.489448 10.714488000000001C8.327136 10.870008 8.273832 10.989408000000001 8.260824000000001 11.226528C8.2422 11.565912 8.25156 11.578656 9.2448 12.566952C10.029264000000001 13.347503999999999 10.102295999999999 13.413696 10.230912 13.460952C10.408224 13.526112 10.591536000000001 13.52616 10.769088 13.461096000000001C10.899624000000001 13.413288 10.980768 13.337664 12.115008 12.207096C12.778872 11.545392 13.35312 10.955400000000001 13.391112 10.896C13.515528000000002 10.701456 13.532231999999999 10.377552000000001 13.428192000000001 10.176384C13.368456 10.060848 13.232832000000002 9.921792 13.108439999999998 9.848472000000001C13.012728000000001 9.792072 12.963888 9.780984 12.780000000000001 9.773928C12.586944 9.766512 12.549984000000002 9.772295999999999 12.432 9.82836M21.469968 9.82752C21.296400000000002 9.898871999999999 21.141504 10.045896 21.067392 10.209624L21.012 10.332 21.005712000000003 15.197568L20.999424 20.06316 21.374712000000002 20.813424L21.75 21.563688 22.117704 20.831856000000002L22.485408000000003 20.1 22.486704 15.234912000000001L22.488 10.3698 22.438056 10.236312C22.296504 9.858 21.852216000000002 9.670392 21.469968 9.82752M12.592968 15.024287999999999C12.529704 15.035304 12.427104000000002 15.077160000000001 12.364968 15.117312000000002C12.302832000000002 15.157464000000001 11.857992000000001 15.584760000000001 11.376408 16.066872L10.500816 16.943448 9.972408 16.417224C9.360504 15.807839999999999 9.301464 15.76848 9 15.76848C8.696184 15.76848 8.464032 15.911328 8.329128 16.181328C8.2668 16.306079999999998 8.256024 16.354536 8.25624 16.50984C8.256648 16.832832000000003 8.262504 16.840632 9.2232 17.797032C10.182744 18.752280000000003 10.184352 18.753504 10.5 18.753504C10.82484 18.753504 10.781400000000001 18.788496000000002 12.149016 17.425176C13.521 16.057512 13.493328 16.091808 13.493328 15.758664C13.493328 15.53796 13.429488000000001 15.383016 13.27668 15.232920000000002C13.0926 15.052104000000002 12.853079999999999 14.979023999999999 12.592968 15.024287999999999"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.024}
			/>
		</svg>
	)
}

function CampaignLaunchIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
			<path
				d="M21.228 0.9477359999999999C20.6076 1.018704 19.963416 1.099416 19.796447999999998 1.1271120000000001C18.453384 1.349784 17.109888 1.947048 16.053528 2.791008C15.639503999999999 3.1218239999999997 15.245256000000001 3.5064480000000002 12.338784 6.415152L9.569592 9.186504000000001 9.254783999999999 9.080400000000001C8.061888 8.6784 7.532976000000001 8.579352 6.588 8.581007999999999C6.1092960000000005 8.581871999999999 5.953200000000001 8.591280000000001 5.664 8.636832C4.41408 8.83368 3.327624 9.3054 2.401752 10.053264C2.057184 10.331592 1.530768 10.851840000000001 1.416936 11.02656C1.119456 11.483136 1.0965120000000002 12.098735999999999 1.359312 12.572976C1.4868240000000001 12.803088 1.7654400000000001 13.070352 1.992 13.179912C2.091 13.227768000000001 2.955048 13.527287999999999 3.9120719999999998 13.845504L5.6521680000000005 14.424047999999999 7.64748 16.422024L9.642816 18.42 10.222152 20.162232C10.540776000000001 21.120432 10.839336000000001 21.983448 10.885608000000001 22.080000000000002C10.985088 22.287576 11.262192 22.579464 11.472 22.697664C11.722104 22.838568 11.906952 22.883976 12.228 22.8834C12.478776 22.882968 12.538752 22.874664 12.69204 22.819152C12.979056 22.715208 13.117824 22.616424000000002 13.460736 22.272000000000002C14.133624 21.596111999999998 14.590368000000002 20.939591999999998 14.921688 20.172C15.441912 18.966696 15.614351999999998 17.661288 15.417792 16.416C15.360192000000001 16.051199999999998 15.24888 15.627839999999999 15.055608000000001 15.038687999999999L14.875416 14.489376000000002 17.957424 11.402688000000001C19.972032000000002 9.384984 21.106896 8.23092 21.234312000000003 8.070312C22.183464 6.873864 22.777632 5.494008 22.978656 4.01928C23.076264 3.30312 23.257392 1.543056 23.24268 1.453656C23.218392 1.306176 23.114232 1.111608 23.008416 1.015992C22.871832 0.8926080000000001 22.721304 0.8334 22.524 0.825456C22.43004 0.821664 21.858816 0.875568 21.228 0.9477359999999999M20.832 2.5086239999999997C20.043792 2.600064 19.720944 2.657784 19.281672 2.785824C18.352656 3.0566400000000002 17.504352 3.5166479999999996 16.7784 4.143312C16.63848 4.264104 14.409888 6.4781759999999995 11.825976 9.063504L7.127952 13.764096 8.710488 15.346488L10.293023999999999 16.92888 12.472512 14.766167999999999C15.812111999999999 11.452296 19.821504 7.426176 19.995336 7.212C20.369328 6.7511280000000005 20.635152 6.333816 20.882016 5.82C21.26808 5.016408 21.421176 4.433784 21.55056 3.2760000000000002C21.590376000000003 2.9196000000000004 21.629256 2.582112 21.63696 2.5260000000000002C21.653976 2.401776 21.737328 2.403576 20.832 2.5086239999999997M6.204 10.094616C4.921392 10.229832 3.8472000000000004 10.719072 2.96904 11.568L2.6835359999999997 11.844 4.269864 12.371832000000001L5.856168 12.89964 7.11648 11.639832L8.376768 10.38 8.274384000000001 10.348896C8.000376 10.265688 7.618272 10.175928 7.391544 10.141536C7.14552 10.104192 6.394536 10.074528 6.204 10.094616M4.992 15.589056C4.310592 15.713016 3.630552 16.115376 3.2292000000000005 16.632C2.752704 17.245344000000003 2.136984 18.703248000000002 1.480776 20.772000000000002C1.1639519999999999 21.770832000000002 1.077 22.091568 1.077 22.261608C1.077 22.658088 1.406544 22.99332 1.7981040000000001 22.995192000000003C1.952184 22.995912 2.196864 22.93224 3.012 22.679304000000002C4.9875359999999995 22.066248 6.513456000000001 21.4524 7.248 20.975208C8.006064 20.482728 8.52 19.528368 8.52 18.613128C8.52 18.331152 8.476896 18.203496 8.325936 18.038304C8.167488 17.864952000000002 8.013767999999999 17.79756 7.776 17.797248C7.564632 17.79696 7.429248 17.848776 7.2732 17.989776C7.115136 18.132576 7.055232 18.275832 7.028952 18.573768C6.993048 18.981168 6.895872 19.23408 6.668760000000001 19.511208C6.455928 19.770912 5.750136 20.124432000000002 4.5600000000000005 20.567496000000002C4.026168 20.76624 2.972256 21.124248 2.959272 21.111288C2.944872 21.096888 3.3396000000000003 19.942776 3.547584 19.391136000000003C3.9749280000000002 18.25776 4.299984 17.617128 4.555896000000001 17.403888000000002C4.798992 17.201328 5.025504 17.104632 5.3825520000000004 17.051016C5.6654160000000005 17.008536 5.7824160000000004 16.954152 5.933808 16.794743999999998C6.286344 16.423632 6.159624 15.83352 5.67936 15.609912000000001C5.5344 15.542424 5.2892399999999995 15.534984000000001 4.992 15.589056M12.41652 16.974L11.165208 18.228 11.692944 19.807008C12.161159999999999 21.207936 12.224904 21.382512 12.258312 21.355008C12.355824 21.274704 12.817824000000002 20.748888 12.960336 20.556C13.619184 19.6644 13.97184 18.590952 13.973591999999998 17.472C13.974216 17.05596 13.947432000000001 16.7526 13.881383999999999 16.428C13.835351999999999 16.201776 13.69968 15.72 13.682015999999999 15.72C13.674216000000001 15.72 13.104743999999998 16.284312 12.41652 16.974"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.024}
			/>
		</svg>
	)
}

function CampaignEndIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24">
			<path
				d="M0.516 0.031872000000000004C0.316944 0.10992 0.16456800000000002 0.24604800000000002 0.06804 0.432L0.012 0.54 0.005928 11.973672L-0.000144 23.407368 0.061176 23.541672000000002C0.14196 23.718576 0.267288 23.845992 0.444 23.930832000000002C0.882144 24.141192 1.40796 23.858832000000003 1.487184 23.370672000000003C1.502952 23.273568 1.511712 22.027656 1.511808 19.866L1.512 16.512 3.876 16.512L6.24 16.512 6.24 17.640936C6.24 18.287232 6.25104 18.868896 6.265824 19.001472C6.37416 19.97304 7.0938 20.758656 8.05536 20.955096C8.270184 20.998968 8.502888 21.000192000000002 15.544535999999999 20.994192L22.812 20.988 22.966632 20.93052C23.31756 20.800104 23.606952000000003 20.569008 23.777664 20.282904C23.87628 20.117592 23.97 19.882488000000002 23.981784 19.770768C23.984952000000003 19.740648 23.99316 20.5476 24 21.564C24.00684 22.5804 24.012408 18.265800000000002 24.01236 11.976C24.012287999999998 5.6862 24.006743999999998 2.0574 24 3.912C23.993256000000002 5.7666 23.985096000000002 7.259352000000001 23.98188 7.2292320000000005C23.969856 7.117128000000001 23.876184000000002 6.8822160000000006 23.777664 6.717096000000001C23.607143999999998 6.431328 23.304816 6.190416000000001 22.960008000000002 6.0655920000000005L22.812 6.0120000000000005 18.907152 6.0056400000000005L15.00228 5.999304 14.99436 4.68564C14.986775999999999 3.424728 14.984376000000001 3.3645840000000002 14.934528000000002 3.187248C14.70144 2.357976 14.062199999999999 1.744104 13.224 1.54464C13.054295999999999 1.504248 12.714096000000001 1.501368 7.2780000000000005 1.494384L1.512 1.486944 1.511808 1.133472C1.511712 0.939072 1.5006240000000002 0.7122 1.487184 0.629328C1.427232 0.2598 1.113648 -0.0027600000000000003 0.7377360000000001 0.001824C0.655392 0.002832 0.5556 0.016344 0.516 0.031872000000000004M0.011904 12C0.011904 18.289800000000003 0.01464 20.862912 0.018000000000000002 17.718C0.02136 14.573112 0.02136 9.426912 0.018000000000000002 6.282C0.01464 3.137112 0.011904 5.7102 0.011904 12M1.512 8.99976L1.512 15 7.5 15L13.488 15 13.488 9.304272000000001C13.488 2.88684 13.507272 3.487728 13.293936 3.2543040000000003C13.222392 3.1760400000000004 13.127832000000001 3.1041119999999998 13.05048 3.0691680000000003L12.924 3.012 7.218 3.00576L1.512 2.99952 1.512 8.99976M14.994264 11.742L14.988 15.972 14.931672 16.080000000000002C14.893272 16.153608000000002 14.350608000000001 16.713552 13.227912 17.838L11.58048 19.488 17.02224 19.488C20.015208 19.488 22.464000000000002 19.482552000000002 22.464000000000002 19.475904C22.464000000000002 19.469256 21.549528000000002 18.435144 20.431848 17.177904C19.314144 15.920664 18.351912000000002 14.818296 18.29352 14.728224C18.037656 14.333424 17.942808 14.001287999999999 17.942808 13.5C17.942808 13.002264 18.039936 12.663024 18.295199999999998 12.269207999999999C18.352656 12.180528 19.314144 11.079336 20.431848 9.822096C21.549528000000002 8.564856 22.464000000000002 7.530744 22.464000000000002 7.524096000000001C22.464000000000002 7.517448 20.78472 7.5120000000000005 18.732264 7.5120000000000005L15.000528000000001 7.5120000000000005 14.994264 11.742M23.956848 7.8660000000000005C23.918784 8.048592000000001 23.787288 8.299728 23.633015999999998 8.484408C23.564256 8.566704000000001 22.6278 9.623232 21.552 10.832208C20.186136 12.367176 19.57428 13.071743999999999 19.524 13.16748C19.46112 13.28724 19.452 13.32936 19.452 13.5C19.452 13.67064 19.46112 13.712760000000001 19.524 13.83252C19.57428 13.92828 20.185752 14.632272 21.552 16.167432C22.6278 17.376216 23.567568 18.43752 23.64036 18.525864C23.793648 18.711864 23.912952 18.940752 23.961744 19.142424000000002C23.991744 19.26636 23.996256000000002 18.564216000000002 23.998008000000002 13.506C23.999112 10.328088 23.996760000000002 7.728 23.992800000000003 7.728C23.98884 7.728 23.972664 7.790088 23.956848 7.8660000000000005M7.75716 17.718L7.764 18.924 7.821168 19.05048C7.892952 19.209312 8.093472 19.396992 8.247432 19.449456C8.338152000000001 19.480368000000002 8.4678 19.488 8.902344 19.488L9.444144 19.488 10.932 18L12.419856000000001 16.512 10.085088 16.512L7.75032 16.512 7.75716 17.718"
				stroke="none"
				fill="currentColor"
				fillRule="evenodd"
				strokeWidth={0.024}
			/>
		</svg>
	)
}
